import {compact} from '@benchling/js-utils';
import {produce} from 'immer';

import * as analytics from 'analytics/Analytics';
import {standardAnalyticsMetadataToDict} from 'analytics/Util';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS, ActionOrigin} from 'constants/AnalyticsConstants';
import {FlowchartActionFunction} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  FlowchartAnalyticsMetadata,
  MODIFICATION_ACTION,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartEditor';
import {
  ProcessFlowchartInteractiveEdge,
  ProcessFlowchartInteractiveNode,
  ProcessInteractiveFlowchart,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  CreateOrUpdateRouterFunctionSpec,
  RouterConfig,
} from 'processes/modals/router-config/RouterConfigConstants';

import {ProcessFlowchartRouterNode} from '../ProcessFlowchartNodeDetailType';
import {isRouterNode} from '../util/getNodeDetailsTypeForInteractiveNode';

export function isNodeUsingDownstreamEdgeConfig(edgeId: string, node: ProcessFlowchartRouterNode): boolean {
  if (edgeId == null) {
    return (
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      node.routerConfig.functionSpecs.some((functionSpec) => functionSpec.edgeConfigReference === null) || // eslint-disable-next-line benchlint/no-eqeqeq-null
      node.routerConfig.defaultFunctionSpec?.edgeConfigReference === null
    );
  }

  return (
    node.routerConfig.functionSpecs.some(
      (functionSpec) => functionSpec.edgeConfigReference?.clientId === edgeId
    ) || node.routerConfig.defaultFunctionSpec?.edgeConfigReference?.clientId === edgeId
  );
}

export function isNodeUsingUpstreamEdgeConfig(
  edgeId: string | null,
  node: ProcessFlowchartRouterNode
): boolean {
  if (edgeId == null) {
    return false;
  }
  const incomingEdges = node.incomingEdgeConnection?.incomingEdges;
  if (incomingEdges != null) {
    return incomingEdges.some((incomingEdgeId: string) => incomingEdgeId === edgeId);
  }
  return false;
}

function isEdgeUsedByFunction(
  functionSpec: CreateOrUpdateRouterFunctionSpec | null,
  edgeId: string | null
): boolean {
  if (functionSpec == null) {
    return false;
  }

  return (functionSpec.edgeConfigReference?.clientId ?? null) === edgeId;
}

/**
 * This removes function specs being used by an edge. If a default function spec
 * is removed, we also remove all the non-default function specs.
 *
 *
 * @param edgeId an ID for a flowchart edge; the router config modal currently accepts
 * null edge config references for functions going to a flowchart output, so this is nullable.
 * @param routerConfig the router config to update.
 *
 * This does not modify the original router config, returns a new updated router config.
 */
export function removeFunctionSpecsUsingEdge(edgeClientId: string, routerConfig: RouterConfig): RouterConfig {
  let updatedFunctionSpecs: Array<CreateOrUpdateRouterFunctionSpec>;
  const updatedDefaultFunctionSpec = isEdgeUsedByFunction(routerConfig.defaultFunctionSpec, edgeClientId)
    ? null
    : routerConfig.defaultFunctionSpec;

  if (updatedDefaultFunctionSpec == null) {
    updatedFunctionSpecs = [];
  } else {
    updatedFunctionSpecs = routerConfig.functionSpecs.filter(
      (functionSpec) => !isEdgeUsedByFunction(functionSpec, edgeClientId)
    );
  }
  return {
    ...routerConfig,
    functionSpecs: updatedFunctionSpecs,
    defaultFunctionSpec: updatedDefaultFunctionSpec,
  };
}

export function updateRouterNodesUsingEdge(
  edgeClientId: string,
  nodes: Array<ProcessFlowchartInteractiveNode>
): Array<ProcessFlowchartInteractiveNode> {
  const updatedNodes: Array<ProcessFlowchartInteractiveNode> = [];
  for (const node of nodes) {
    const updatedNode = produce(node, (draftNode) => {
      if (isRouterNode(draftNode)) {
        if (isNodeUsingDownstreamEdgeConfig(edgeClientId, draftNode)) {
          draftNode.routerConfig = removeFunctionSpecsUsingEdge(edgeClientId, draftNode.routerConfig);
        } else if (isNodeUsingUpstreamEdgeConfig(edgeClientId, draftNode)) {
          removeAllFunctionSpecs(draftNode);
        }
      }
    });
    updatedNodes.push(updatedNode);
  }
  return updatedNodes;
}

export function removeAllFunctionSpecs(node: ProcessFlowchartRouterNode): void {
  const allDownstreamEdgeIds = compact(
    [...node.routerConfig.functionSpecs, node.routerConfig.defaultFunctionSpec].map(
      (functionSpec) => functionSpec?.edgeConfigReference?.clientId
    )
  );

  for (const downstreamEdgeId of allDownstreamEdgeIds) {
    node.routerConfig = removeFunctionSpecsUsingEdge(downstreamEdgeId, node.routerConfig);
  }
}

/**
 * This deletes only the edge that is specified by the edgeClientId.
 *
 * This does not modify the original flowchart, returns a new updated flowchart object.
 */
export function deleteEdgeFromFlowchart(
  edgeClientId: string,
  flowchart: ProcessInteractiveFlowchart
): ProcessInteractiveFlowchart {
  const updatedEdges = flowchart.edges.filter(({clientId}) => edgeClientId !== clientId);
  assert(
    updatedEdges.length !== flowchart.edges.length,
    `Edge with ID ${edgeClientId} not found in flowchart`
  );
  const updatedNodes = updateRouterNodesUsingEdge(edgeClientId, flowchart.nodes);

  return {
    ...flowchart,
    nodes: updatedNodes,
    edges: updatedEdges,
  };
}

export default function deleteEdgeProcessFlowchartActionFunction(
  flowchart: ProcessInteractiveFlowchart,
  handleDelete: (edgeClientId: string, flowchart: ProcessInteractiveFlowchart) => ProcessInteractiveFlowchart,
  onChangeFlowchart: (flowchart: ProcessInteractiveFlowchart) => void,
  flowchartSchemaId: string,
  editorEventGroupId: string,
  analyticsMetadata: FlowchartAnalyticsMetadata,
  modalType?: string
): FlowchartActionFunction<ProcessFlowchartInteractiveEdge, ProcessInteractiveFlowchart> {
  return {
    icon: 'delete',
    name: 'Delete edge',
    tooltipText: 'Delete edge',
    flowchart,
    updateHandler: onChangeFlowchart,
    onInvoke: ({item, flowchart, updateHandler}) => {
      const edgeId = item.clientId;
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MODIFIED_ITEM_IN_CANVAS, {
        ...standardAnalyticsMetadataToDict(analyticsMetadata),
        action_type: MODIFICATION_ACTION.DELETED_EDGE,
        modified_item: flowchartSchemaId,
        nodes_modified: [],
        edges_modified: [edgeId],
        event_group_id: editorEventGroupId,
        action_origin: ActionOrigin.WORKFLOWS_GRAPH_BUILDER_CANVAS,
        modal_type: modalType,
      });
      const updatedFlowchart = handleDelete(item.clientId, flowchart);
      updateHandler(updatedFlowchart);
    },
  };
}
